import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSeeds } from '../hooks/useSeeds'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AlertCircle, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Seed, SeedVersion } from '../types'
import { LongRunningOpDialog, makeDeleteInterpreter } from '../components/LongRunningOpDialog'
import { PruneVersionsDialog } from '../components/PruneVersionsDialog'

const statusColors: Record<Seed['status'], string> = {
  planted: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  sprouted: 'bg-sky-100 text-sky-800 hover:bg-sky-100',
  growing: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
}

const statusLabels: Record<Seed['status'], string> = {
  planted: 'Planted',
  sprouted: 'Sprouted',
  growing: 'Growing',
}

const statusDescriptions: Record<Seed['status'], string> = {
  planted: 'No shares yet',
  sprouted: 'Has shares, no visits',
  growing: 'Has visits',
}

/**
 * Color tier for "Hydrated N ago" cells. A seed is "hydrated" when a
 * non-admin user visits it (which boot-hydrates the DO if it's cold).
 */
type HydrationTier = 'fresh' | 'stale' | 'dormant'

function hydrationTier(iso: string | null | undefined): HydrationTier {
  if (!iso) return 'dormant'
  const ms = Date.now() - new Date(iso).getTime()
  const days = ms / 86_400_000
  if (days <= 7) return 'fresh'
  if (days <= 30) return 'stale'
  return 'dormant'
}

const hydrationClass: Record<HydrationTier, string> = {
  fresh: 'text-emerald-700 dark:text-emerald-400',
  stale: 'text-orange-600 dark:text-orange-400',
  dormant: 'text-muted-foreground',
}

/** Tailwind classes for the small filled circle next to the status badge. */
const hydrationDotBg: Record<HydrationTier, string> = {
  fresh: 'bg-emerald-500',
  stale: 'bg-orange-500',
  dormant: 'bg-muted-foreground/40',
}

const hydrationLabel: Record<HydrationTier, string> = {
  fresh: 'Fresh (≤7d)',
  stale: 'Stale (≤30d)',
  dormant: 'Dormant (>30d or never)',
}

function hydratedSummary(iso: string | null | undefined): string {
  if (!iso) return 'never visited by a non-admin user'
  const d = new Date(iso)
  return `last visit: ${timeAgo(iso)} ago (${d.toLocaleString()})`
}

/** Date helper: dd/mm/yy. Used by the Planted + Cultivated columns. */
function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d`
  const months = days / 30.44
  if (months < 12) return `${months.toFixed(1)}mo`
  const years = months / 12
  return `${years.toFixed(1)}y`
}

export function Dashboard() {
  const { seeds, loading, error, refetch } = useSeeds()
  const navigate = useNavigate()

  // Per-row prune flow: load versions on demand, open PruneVersionsDialog.
  const [pruneSeed, setPruneSeed] = useState<string | null>(null)
  const [pruneVersions, setPruneVersions] = useState<SeedVersion[]>([])
  async function openPrune(seedName: string) {
    try {
      const res = await fetch(`/api/seeds/${seedName}/versions`)
      const data = await res.json() as { versions?: SeedVersion[] }
      setPruneVersions(data.versions ?? [])
      setPruneSeed(seedName)
    } catch { /* surface in dialog as empty list */ }
  }

  // Per-row delete flow: streaming dialog.
  const [deleteSeedTarget, setDeleteSeedTarget] = useState<string | null>(null)
  const rowDeleteInterpreter = useMemo(() => makeDeleteInterpreter(), [deleteSeedTarget])

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Seeds</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Demo apps discovered from the repository. Create new seeds via the TUI or git.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="font-medium uppercase tracking-wide text-foreground/70">Status</span>
          {(Object.keys(statusLabels) as Seed['status'][]).map(s => (
            <span key={s} className="inline-flex items-center gap-1.5">
              <Badge variant="secondary" className={cn('rounded-full border-transparent text-[10px] px-1.5 py-0', statusColors[s])}>
                {statusLabels[s]}
              </Badge>
              <span>{statusDescriptions[s]}</span>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="font-medium uppercase tracking-wide text-foreground/70">Hydrated (last visit)</span>
          {(['fresh', 'stale', 'dormant'] as const).map(t => (
            <span key={t} className="inline-flex items-center gap-1.5">
              <span className={cn('inline-block size-2 rounded-full', hydrationDotBg[t])} />
              <span className={hydrationClass[t]}>{hydrationLabel[t]}</span>
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <Card>
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading seeds: {error}</AlertDescription>
        </Alert>
      ) : seeds.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-sm font-semibold text-foreground">No seeds discovered</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Create a seed via the TUI (<code>pnpm seeds</code>) or push one to the repository.
          </p>
        </div>
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-10 bg-muted/70 dark:bg-muted/40">Name</TableHead>
                <TableHead>Repo</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Apps</TableHead>
                <TableHead>Versions</TableHead>
                <TableHead>SDK</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Planted</TableHead>
                <TableHead>Cultivated</TableHead>
                <TableHead className="text-right sticky right-0 z-10 bg-muted/70 dark:bg-muted/40">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {seeds.map((seed) => {
                const apps = (seed.apps || []).filter(a => a !== 'worker')

                return (
                  <TableRow
                    key={seed.name}
                    className="group cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/seeds/${seed.name}`)}
                  >
                    <TableCell className="sticky left-0 z-10 bg-background group-hover:bg-muted/50">
                      <span className="font-medium text-foreground">
                        {seed.displayName}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {seed.repo ? (
                        // The org is the same for every seed; the repository name is
                        // what tells two of them apart.
                        <a
                          href={`https://github.com/${seed.repo}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={seed.repo}
                          onClick={e => e.stopPropagation()}
                          // Capped at the width of `seed-harness-ci` (15 monospace
                          // characters): longer names were widening the column until
                          // the seed names beside it wrapped. The full name is on hover.
                          className="block max-w-[15ch] truncate font-mono text-xs hover:text-foreground hover:underline"
                        >
                          {seed.repo.split('/').pop()}
                        </a>
                      ) : (
                        <span title="No deploy has recorded a repository for this seed yet">—</span>
                      )}
                    </TableCell>
                    {/* The one column that gives way. `w-full` hands it whatever
                        width the fixed columns leave and `max-w-0` stops its text
                        from claiming more, so the table fits its card instead of
                        sliding Cultivated under the pinned Actions column. It never
                        collapses below its own header; the full text is on hover. */}
                    <TableCell
                      className="w-full max-w-0 truncate text-muted-foreground"
                      title={seed.description}
                    >
                      {seed.description}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="secondary"
                          className={cn('rounded-full border-transparent', statusColors[seed.status])}
                        >
                          {statusLabels[seed.status]}
                        </Badge>
                        <span
                          className={cn('inline-block size-2.5 rounded-full shrink-0', hydrationDotBg[hydrationTier(seed.lastHydratedAt)])}
                          title={hydratedSummary(seed.lastHydratedAt)}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {apps.length > 0 ? (
                        <span className="text-sm text-muted-foreground">{apps.length}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell
                      className="text-muted-foreground text-sm whitespace-nowrap font-mono"
                      title={
                        seed.versionCount
                          ? `Current: v${seed.version ?? '—'} · ${seed.versionCount} version(s) total`
                          : undefined
                      }
                    >
                      {seed.version ? `v${seed.version}` : '—'}
                      {seed.versionCount ? <span className="text-muted-foreground/60"> ({seed.versionCount})</span> : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap font-mono">
                      {seed.sdkVersion || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap font-mono">
                      {seed.agentVersion || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap font-mono">
                      {shortDate(seed.createdAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap font-mono">
                      {shortDate(seed.updatedAt)}
                    </TableCell>
                    <TableCell
                      className="text-right sticky right-0 z-10 bg-background group-hover:bg-muted/50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/40"
                          onClick={() => openPrune(seed.name)}
                          title={`Prune old versions of ${seed.displayName}`}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => setDeleteSeedTarget(seed.name)}
                          title={`Delete seed ${seed.displayName}`}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Per-row Prune dialog (fetches versions on demand). */}
      <PruneVersionsDialog
        open={pruneSeed !== null}
        onClose={() => setPruneSeed(null)}
        onComplete={() => refetch()}
        seedName={pruneSeed ?? ''}
        versions={pruneVersions}
      />

      {/* Per-row Delete dialog (streaming, force-cascade). */}
      <LongRunningOpDialog
        open={deleteSeedTarget !== null}
        onClose={() => setDeleteSeedTarget(null)}
        onComplete={() => refetch()}
        title={`Delete seed ${deleteSeedTarget ?? ''}`}
        description="Drops every version (workers + R2 + DOs), every share, every materialized blob, all guide media and the seed’s guide approval history. This cannot be undone."
        confirmLabel="Confirm delete"
        warning="Active shares will be force-deleted."
        request={{
          method: 'POST',
          url: `/api/seeds/${deleteSeedTarget ?? ''}/delete/stream?force=true`,
        }}
        interpretEvent={rowDeleteInterpreter}
      />
    </div>
  )
}
